import type { CandidateSummary } from "@/api/preview";
import type { PreviewManualSessionState } from "@/api/previewManual";
import type { PreviewHistoryItem } from "@/api/previewHistory";
import type { PreviewSimRunStatus, PreviewSimTurnStat } from "@/api/previewSim";
import { isCurrent } from "@/lib/candidateFreshness";
import {
  describePosition,
  readTraceEvent,
  safetyOutcomeTone,
  type ClassificationEvent,
  type CriterionEvent,
  type RoutingEvent,
  type RuntimePosition,
  type SafetyEvent,
  type TraceEvent,
  type TransitionEvent,
  type TurnEvent,
  type WhyNotEvent,
} from "@/lib/previewDecisions";
import { candidateDisplayVersion, type PreviewRunView } from "@/lib/previewInsights";

/**
 * Pure view-model of the Lab workspace: one run's events regrouped per turn,
 * the per-turn badges, the turn-by-turn alignment of two runs, and the small
 * list/selection helpers the panes share. No React, no network.
 */

export type LabBadgeKind = "classification" | "threshold" | "routing" | "transition" | "safety";

export const LAB_BADGE_KINDS: readonly LabBadgeKind[] = [
  "classification",
  "threshold",
  "routing",
  "transition",
  "safety",
];

export const LAB_BADGE_LABELS: Record<LabBadgeKind, string> = {
  classification: "Classification",
  threshold: "Threshold",
  routing: "Routing",
  transition: "Flow",
  safety: "Safety",
};

export const LAB_BADGE_HELP: Record<LabBadgeKind, string> = {
  classification: "Classification: a classifier ran at this turn.",
  threshold: "Threshold: a threshold was checked at this turn; (n) = not met.",
  routing: "Routing: the flow chose the next track or step at this turn.",
  transition: "Flow: the conversation moved to another stage or track at this turn.",
  safety:
    "Safety: safety checks on the patient message and Liv's reply; (n) = needs attention.",
};

export const LAB_POSITION_HELP =
  "Agent / track: where the conversation was after this turn.";

export type LabTurnBadge = {
  kind: LabBadgeKind;
  count: number;
  attention: number;
};

export type LabPromptCallRecord = { eventRef: string; sequence: number; record: unknown };

export type LabTurn = {
  turnNumber: number;
  sequence: number;
  turnEvent: TurnEvent | null;
  before: RuntimePosition | null;
  after: RuntimePosition | null;
  agent: string | null;
  track: string | null;
  events: TraceEvent[];
  classifications: ClassificationEvent[];
  criteria: CriterionEvent[];
  routings: RoutingEvent[];
  whyNots: WhyNotEvent[];
  transitions: TransitionEvent[];
  safety: SafetyEvent[];
  promptCalls: LabPromptCallRecord[];
  badges: LabTurnBadge[];
};

export type LabTurns = {
  turns: LabTurn[];
  preTurnEvents: TraceEvent[];
  allEvents: TraceEvent[];
  unreadable: number;
};

export const EMPTY_LAB_TURNS: LabTurns = {
  turns: [],
  preTurnEvents: [],
  allEvents: [],
  unreadable: 0,
};

function isCleanSafety(event: SafetyEvent): boolean {
  return safetyOutcomeTone(event.detail.outcome) === "success";
}

export function labTurnBadges(turn: Omit<LabTurn, "badges">): LabTurnBadge[] {
  const all: LabTurnBadge[] = [
    { kind: "classification", count: turn.classifications.length, attention: 0 },
    {
      kind: "threshold",
      count: turn.criteria.length,
      attention: turn.criteria.filter((c) => c.detail.passed === false).length,
    },
    { kind: "routing", count: turn.routings.length, attention: 0 },
    { kind: "transition", count: turn.transitions.length, attention: 0 },
    {
      kind: "safety",
      count: turn.safety.length,
      attention: turn.safety.filter((s) => !isCleanSafety(s)).length,
    },
  ];
  return LAB_BADGE_KINDS.flatMap((kind) =>
    all.filter((badge) => badge.kind === kind && badge.count > 0),
  );
}

function byRecordOrder(a: TraceEvent, b: TraceEvent): number {
  return a.sequence - b.sequence || (a.eventRef < b.eventRef ? -1 : a.eventRef > b.eventRef ? 1 : 0);
}

function rawEventRef(record: unknown): string | null {
  if (typeof record !== "object" || record === null) {
    return null;
  }
  const ref = (record as Record<string, unknown>)["eventRef"];
  return typeof ref === "string" ? ref : null;
}

/**
 * Regroups a run's compact event records per turn. Each record is parsed once.
 * An event with a turn number belongs to that turn; one without belongs to the
 * turn in effect at its sequence, or to `preTurnEvents` when none precedes it.
 */
export function buildLabTurns(records: readonly unknown[]): LabTurns {
  const events: TraceEvent[] = [];
  let unreadable = 0;
  for (const record of records) {
    const reading = readTraceEvent(record);
    if (reading.kind === "event") {
      events.push(reading.event);
    } else {
      unreadable += 1;
    }
  }
  events.sort(byRecordOrder);

  const rawByRef = new Map<string, unknown>();
  for (const record of records) {
    const ref = rawEventRef(record);
    if (ref !== null) {
      rawByRef.set(ref, record);
    }
  }

  const grouped = new Map<number, TraceEvent[]>();
  const preTurnEvents: TraceEvent[] = [];
  let turnEventCount = 0;
  let inEffect: number | null = null;
  const add = (turnNumber: number, event: TraceEvent) => {
    const list = grouped.get(turnNumber);
    if (list === undefined) {
      grouped.set(turnNumber, [event]);
    } else {
      list.push(event);
    }
  };

  for (const event of events) {
    if (event.family === "turn") {
      turnEventCount += 1;
      inEffect = event.coordinate.turnNumber ?? turnEventCount;
      add(inEffect, event);
      continue;
    }
    const turnNumber = event.coordinate.turnNumber ?? inEffect;
    if (turnNumber === null) {
      preTurnEvents.push(event);
    } else {
      add(turnNumber, event);
    }
  }

  const turns = [...grouped.entries()]
    .sort(([a], [b]) => a - b)
    .map(([turnNumber, turnEvents]): LabTurn => {
      const turnEvent =
        turnEvents.find((e): e is TurnEvent => e.family === "turn") ?? null;
      const before = turnEvent?.detail.before ?? null;
      const after = turnEvent?.detail.after ?? null;
      const promptCalls: LabPromptCallRecord[] = [];
      for (const event of turnEvents) {
        if (event.kind === "prompt_call") {
          promptCalls.push({
            eventRef: event.eventRef,
            sequence: event.sequence,
            record: rawByRef.get(event.eventRef) ?? null,
          });
        }
      }
      const base: Omit<LabTurn, "badges"> = {
        turnNumber,
        sequence: (turnEvent ?? turnEvents[0]!).sequence,
        turnEvent,
        before,
        after,
        agent: after?.agent ?? before?.agent ?? null,
        track: after?.track ?? before?.track ?? null,
        events: turnEvents,
        classifications: turnEvents.filter((e): e is ClassificationEvent => e.family === "classification"),
        criteria: turnEvents.filter((e): e is CriterionEvent => e.family === "criterion_threshold"),
        routings: turnEvents.filter((e): e is RoutingEvent => e.family === "routing"),
        whyNots: turnEvents.filter((e): e is WhyNotEvent => e.family === "why_not"),
        transitions: turnEvents.filter((e): e is TransitionEvent => e.family === "runtime_transition"),
        safety: turnEvents.filter((e): e is SafetyEvent => e.family === "safety"),
        promptCalls,
      };
      return { ...base, badges: labTurnBadges(base) };
    });

  return { turns, preTurnEvents, allEvents: events, unreadable };
}

export function labTurnByNumber(
  turns: readonly LabTurn[],
  turnNumber: number | null,
): LabTurn | null {
  return turnNumber === null ? null : (turns.find((t) => t.turnNumber === turnNumber) ?? null);
}

export type LabTurnDiff = {
  classification: boolean;
  threshold: boolean;
  routing: boolean;
  transition: boolean;
  safety: boolean;
  any: boolean;
};

function signature(items: readonly string[], sort: boolean): string {
  return JSON.stringify(sort ? [...items].sort() : items);
}

function turnSignatures(turn: LabTurn): Record<Exclude<keyof LabTurnDiff, "any">, string> {
  return {
    classification: signature(
      turn.classifications.map(
        (e) =>
          `${e.detail.classifierId}|${e.detail.result.contentHash ?? e.detail.result.inline ?? e.detail.result.state}`,
      ),
      true,
    ),
    threshold: signature(
      turn.criteria.map((e) => `${e.detail.criterionId}|${e.detail.passed}`),
      true,
    ),
    routing: signature(
      turn.routings.map(
        (e) => `${e.detail.decisionId}|${e.detail.selectedChoice}|${e.detail.reasonCode}`,
      ),
      true,
    ),
    transition: signature(
      turn.transitions.map((e) => describePosition(e.detail.to)),
      false,
    ),
    safety: signature(
      turn.safety.map((e) => `${e.detail.mechanism}|${e.detail.outcome}`),
      true,
    ),
  };
}

export function diffLabTurns(a: LabTurn, b: LabTurn): LabTurnDiff {
  const x = turnSignatures(a);
  const y = turnSignatures(b);
  const diff = {
    classification: x.classification !== y.classification,
    threshold: x.threshold !== y.threshold,
    routing: x.routing !== y.routing,
    transition: x.transition !== y.transition,
    safety: x.safety !== y.safety,
  };
  return { ...diff, any: Object.values(diff).some(Boolean) };
}

export type LabAlignedTurn = {
  turnNumber: number;
  a: LabTurn | null;
  b: LabTurn | null;
  onlyIn: "a" | "b" | null;
  diff: LabTurnDiff | null;
};

export function alignLabRuns(a: readonly LabTurn[], b: readonly LabTurn[]): LabAlignedTurn[] {
  const numbers = [...new Set([...a, ...b].map((t) => t.turnNumber))].sort((x, y) => x - y);
  return numbers.map((turnNumber) => {
    const left = labTurnByNumber(a, turnNumber);
    const right = labTurnByNumber(b, turnNumber);
    return {
      turnNumber,
      a: left,
      b: right,
      onlyIn: left !== null && right === null ? "a" : left === null && right !== null ? "b" : null,
      diff: left !== null && right !== null ? diffLabTurns(left, right) : null,
    };
  });
}

/** "12/40" */
export function labProgress(sim: Pick<PreviewSimRunStatus, "turnsCompleted" | "maxTurns">): string {
  return `${sim.turnsCompleted}/${sim.maxTurns}`;
}

/** Sim runs first, then runs whose status is still unknown, then known non-sim runs; input order kept inside each group. */
export function sortLabRuns(
  items: readonly PreviewHistoryItem[],
  sims: ReadonlyMap<string, PreviewSimRunStatus | null | undefined>,
): PreviewHistoryItem[] {
  const rank = (item: PreviewHistoryItem): number => {
    const sim = sims.get(item.previewRunRef);
    return sim === undefined ? 1 : sim === null ? 2 : 0;
  };
  return items
    .map((item, index) => ({ item, index, group: rank(item) }))
    .sort((x, y) => x.group - y.group || x.index - y.index)
    .map((entry) => entry.item);
}

export function sortCandidatesNewestFirst(items: readonly CandidateSummary[]): CandidateSummary[] {
  return items
    .map((item, index) => ({ item, index, time: Date.parse(item.createdAt) }))
    .sort((x, y) => {
      const a = Number.isNaN(x.time) ? -Infinity : x.time;
      const b = Number.isNaN(y.time) ? -Infinity : y.time;
      return b === a ? x.index - y.index : b > a ? 1 : -1;
    })
    .map((entry) => entry.item);
}

/** The newest current, unexpired candidate; else the newest; else null. */
export function defaultLabCandidateId(items: readonly CandidateSummary[]): string | null {
  const sorted = sortCandidatesNewestFirst(items);
  const usable = sorted.find((c) => isCurrent(c) && c.validity?.state !== "expired");
  return (usable ?? sorted[0])?.candidateId ?? null;
}

export function labCandidateLabel(c: CandidateSummary): string {
  return candidateDisplayVersion(c.revision) ?? "Test version (unnumbered)";
}

export function labCaptureWarning(
  run: { captureState: string; lastSequence?: number | null } | null,
): string | null {
  return run !== null && run.captureState === "incomplete"
    ? `Trace capture cap reached at event ${run.lastSequence ?? "?"} — later events missing`
    : null;
}

export function labSimStatForTurn(
  sim: PreviewSimRunStatus | null,
  turnNumber: number,
): PreviewSimTurnStat | null {
  return sim?.simStats.find((s) => s.turn === turnNumber) ?? null;
}

/**
 * A patient line the clinician sent that the run's events do not carry yet.
 * It stays at the end of the conversation until the turn it expects is read,
 * or is marked `failed` when the send was not accepted.
 */
export type LabPendingLine = {
  previewRunRef: string;
  text: string;
  /** The turn number the line was sent for. */
  expectedTurn: number;
  /** `unconfirmed`: the service did not say whether the line was delivered. */
  status: "sending" | "sent" | "failed" | "unconfirmed";
};

/** What the route hands every pane for ONE run (built by useLabRunData). */
export type LabRunData = {
  previewRunRef: string;
  view: PreviewRunView | null;
  sim: PreviewSimRunStatus | null;
  /** The candidate revision number the run was dispatched on; null when the service could not resolve it. */
  candidateRevision?: number | null;
  /** Set for a manual session (the clinician drives the turns); null otherwise. */
  manual?: PreviewManualSessionState | null;
  lab: LabTurns;
  /** The compact event records as read (no bodies); the run model builds on them. */
  records: readonly unknown[];
  contentState: "pending" | "ready" | "unavailable" | "denied" | "error";
  complete: boolean;
  live: boolean;
  progress: string | null;
  stale: boolean;
  error: unknown | null;
  refetch: () => void;
};
